import axios from "axios";
import router from "@/router";
import { loginManager } from "@/services/security/login-manager";
import { useUserStore } from "@/store/user/user";
import { useThemeStore } from "@/store/theme/theme";
import { useTrainingCourseStore } from "@/store/training-course/training-course";
import { analyticsTagsConstantes } from "@/constantes/analyticsTags";
import { moduleConstantes } from "@/constantes/module";

const ENDPOINT_PATH = "/app/front/analytics";
const TAG_ID_PATTERN = /^[A-Za-z0-9]{16}$/;

const MODULE_TOGGLE_TAG_IDS = {
  [moduleConstantes.MODULE_STUDEA_ABSENCE]: analyticsTagsConstantes.PILOT_HOME_MODULE_ABSENCE_ENABLED,
  [moduleConstantes.MODULE_STUDEA_GRADEBOOK]: analyticsTagsConstantes.PILOT_HOME_MODULE_GRADEBOOK_ENABLED,
  [moduleConstantes.MODULE_STUDEA_SKILL]: analyticsTagsConstantes.PILOT_HOME_MODULE_SKILL_ENABLED,
  [moduleConstantes.MODULE_STUDEA_COURSE]: analyticsTagsConstantes.PILOT_HOME_MODULE_COURSE_ENABLED,
  [moduleConstantes.MODULE_STUDEA_DOCUMENT]: analyticsTagsConstantes.PILOT_HOME_MODULE_DOCUMENT_ENABLED,
  [moduleConstantes.MODULE_STUDEA_REGISTRATION]: analyticsTagsConstantes.PILOT_HOME_MODULE_REGISTRATION_ENABLED,
  [moduleConstantes.MODULE_STUDEA_NOTEPAD]: analyticsTagsConstantes.PILOT_HOME_MODULE_NOTEPAD_ENABLED,
  [moduleConstantes.MODULE_STUDEA_WEBLINKS]: analyticsTagsConstantes.PILOT_HOME_MODULE_WEBLINKS_ENABLED,
  [moduleConstantes.MODULE_STUDEA_MESSAGE]: analyticsTagsConstantes.PILOT_HOME_MODULE_MESSAGE_ENABLED,
  [moduleConstantes.MODULE_STUDEA_QUESTIONNAIRE]: analyticsTagsConstantes.PILOT_HOME_MODULE_QUESTIONNAIRE_ENABLED,
  [moduleConstantes.MODULE_STUDEA_TRIAD_DECLARE_CONTRACT_RUPTURE]:
    analyticsTagsConstantes.PILOT_HOME_MODULE_CONTRACT_BREAK_ENABLED,
};

const EDITOR_ACTION_TAG_IDS = {
  "list-bullet": analyticsTagsConstantes.EDITOR_LIST_BULLET,
  "list-numbered": analyticsTagsConstantes.EDITOR_LIST_NUMBERED,
  link: analyticsTagsConstantes.EDITOR_LINK_INSERT,
  image: analyticsTagsConstantes.EDITOR_IMAGE_INSERT,
  media: analyticsTagsConstantes.EDITOR_MEDIA_INSERT,
};

class AnalyticsManager {
  get enabled() {
    return process.env.VUE_APP_ANALYTICS_ENABLED === "1" || process.env.VUE_APP_ANALYTICS_ENABLED === "true";
  }

  buildPayload(payload) {
    const trainingCourseStore = useTrainingCourseStore();
    const trainingCourseId = trainingCourseStore?.trainingCourse?.id ?? null;

    if (!trainingCourseId) {
      return payload ?? null;
    }

    if (payload && "trainingCourseId" in payload) {
      return payload;
    }

    return { ...(payload ?? {}), trainingCourseId };
  }

  send(options = {}) {
    if (!this.enabled) {
      return;
    }

    const userStore = useUserStore();
    if (!userStore?.user?.id) {
      return;
    }

    if (!loginManager.token || !loginManager.apiPath) {
      return;
    }

    const themeStore = useThemeStore();

    const tagId = options.tagId ?? null;
    if (!tagId || !TAG_ID_PATTERN.test(tagId)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[analytics] invalid or missing tagId", tagId);
      }
      return;
    }

    const route = router.currentRoute.value;
    const module = "module" in options ? options.module : (route?.meta?.code ?? null);
    const frontUrl = "frontUrl" in options ? options.frontUrl : (route?.fullPath ?? "");
    const payload = this.buildPayload(options.payload);

    const body = {
      tagId,
      module,
      frontUrl,
      payload,
    };

    const url = loginManager.apiPath + "/" + themeStore.locale + ENDPOINT_PATH;
    const headers = {
      "Content-Type": "application/json;charset=utf-8",
      Authorization: "Bearer " + loginManager.token,
    };

    // Un clic sur un lien de téléchargement démarre une navigation qui annule les requêtes XHR en
    // vol : fetch keepalive survit à cette navigation, contrairement à axios.
    if (options.keepalive && typeof fetch === "function") {
      fetch(url, { method: "POST", headers, body: JSON.stringify(body), keepalive: true }).catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[analytics] send failed", error?.message ?? error);
        }
      });
      return;
    }

    axios.post(url, body, { headers }).catch((error) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[analytics] send failed", error?.response?.status ?? error?.message ?? error);
      }
    });
  }

  trackClick(binding) {
    const value = binding?.value;
    if (typeof value === "string") {
      this.send({ tagId: value });
      return;
    }
    if (value && typeof value === "object") {
      this.send({
        tagId: value.tagId ?? null,
        ...("module" in value ? { module: value.module } : {}),
        payload: value.payload ?? null,
        keepalive: value.keepalive ?? false,
      });
    }
  }

  trackCustom(tagId, options = {}) {
    this.send({
      tagId,
      ...("module" in options ? { module: options.module } : {}),
      payload: options.payload ?? null,
      keepalive: options.keepalive ?? false,
    });
  }

  trackEditorAction(action) {
    const tagId = EDITOR_ACTION_TAG_IDS[action];
    if (!tagId) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[analytics] unknown editor action", action);
      }
      return;
    }
    this.send({ tagId });
  }

  trackModuleToggle(moduleCode, payload = {}) {
    const tagId = MODULE_TOGGLE_TAG_IDS[moduleCode];
    if (!tagId) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[analytics] unknown module toggle", moduleCode);
      }
      return;
    }
    this.send({ tagId, module: moduleCode, payload });
  }

  isSameRoute(to, from) {
    if (!from?.name || to?.name !== from.name) {
      return false;
    }

    return JSON.stringify(to.params ?? {}) === JSON.stringify(from.params ?? {});
  }

  trackPageView(route, from = null, failure = null) {
    if (!route?.meta?.trackPageView) {
      return;
    }
    // Vue Router déclenche afterEach même quand la navigation est refusée (doublon) ou quand elle
    // ne fait que réécrire la query de la route courante : un seul pageview par consultation.
    if (failure || this.isSameRoute(route, from)) {
      return;
    }
    if (!route.meta.trackPageViewTagId && process.env.NODE_ENV !== "production") {
      console.warn("[analytics] trackPageView enabled without trackPageViewTagId", route.fullPath);
    }
    this.send({
      tagId: route.meta.trackPageViewTagId ?? null,
      module: route.meta.code ?? null,
      frontUrl: route.fullPath ?? "",
    });
  }
}

export const analyticsManager = new AnalyticsManager();
